/* Gleva · Direction B · gvb-collection.js — collection + search results behaviour (BUILD-SPEC §2.5).
   ES module, loaded by sections/main-collection.liquid and sections/main-search.liquid. Everything is delegated,
   so it keeps working after Impact's `facet:update` swaps the section (filters, sort, sub-categories, pages).
   - Filter chips / Clear filters ([data-gvb-facet]) and the Sort sheet ([data-gvb-sort]) dispatch Impact's
     `facet:update` (Impact fetches the section, swaps it and updates the URL); focus returns to the same control
     and #status-announcement reads "Showing 6 of 30".
   - With "Hide sold-out products" on ([data-gvb-instock]) chips and sort add filter.v.availability=1.
   - Help me choose tabs (role=tab, arrow keys, Home/End); the head's "Help me choose" falls back to #faq.
   - Sticky filter bar hairline (.is-stuck); white-ground card photos get the cream studio ground (.is-studio).
   - Quick buy (gvb-card "Choose", Help me choose): after an add the sheet closes and the toast shows (gvb.js);
     with "Open the cart drawer" selected in Theme settings the drawer opens instead. */
import { G } from 'gvb';

const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
let pending = null;

/* ---------- facet updates ---------- */
const sectionFor = (el) => {
  const sec = el.closest('.shopify-section');
  if (sec) return sec;
  const drawer = el.closest('x-drawer[id^="gvb-sort-"]');
  return drawer ? document.getElementById('shopify-section-' + drawer.id.slice(9)) : null;
};
const isSearch = () => !!document.querySelector('.gvb-search-page');
const go = (sec, href, key) => {
  if (!sec) { window.location.href = href; return; }
  const url = new URL(href, window.location.origin);
  url.searchParams.delete('page');
  /* "Hide sold-out products": every chip and sort lands on Shopify's In stock filter, so counts and pages are exact */
  const root = sec.querySelector('[data-gvb-coll]');
  if (root && root.hasAttribute('data-gvb-instock') && !url.searchParams.has('filter.v.availability')) url.searchParams.set('filter.v.availability', '1');
  url.searchParams.set('section_id', sec.id.replace('shopify-section-', ''));
  pending = { id: sec.id, key };
  sec.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
};

document.addEventListener('click', (e) => {
  const chip = e.target.closest('[data-gvb-facet]');
  if (chip) {
    e.preventDefault();
    go(sectionFor(chip), chip.getAttribute('data-gvb-facet'), chip.dataset.key || '');
    return;
  }
  /* Impact's <a is="facet-link"> (sub-categories, pages) handle themselves: remember the key for focus */
  const link = e.target.closest('a[is="facet-link"][data-key]');
  if (link) { const sec = sectionFor(link); if (sec) pending = { id: sec.id, key: link.dataset.key }; }
}, true);

/* ---------- sort sheet (radios inside Impact's <x-drawer>) ---------- */
const pickSort = async (btn) => {
  const list = btn.closest('[data-gvb-sortlist]');
  if (!list) return;
  $$('[role="radio"]', list).forEach((r) => { const on = r === btn; r.setAttribute('aria-checked', on ? 'true' : 'false'); r.tabIndex = on ? 0 : -1; });
  const drawer = btn.closest('x-drawer');
  const sec = sectionFor(btn);
  if (drawer && typeof drawer.hide === 'function' && drawer.open) await drawer.hide();
  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', btn.dataset.gvbSort);
  if (list.dataset.kind === 'search' || isSearch()) url.searchParams.set('type', 'product');
  go(sec, url.toString(), 'sort');
};
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-gvb-sort]');
  if (btn) { e.preventDefault(); pickSort(btn); }
});
document.addEventListener('keydown', (e) => {
  const btn = e.target.closest && e.target.closest('[data-gvb-sortlist] [role="radio"]');
  if (!btn) return;
  const radios = $$('[role="radio"]', btn.closest('[data-gvb-sortlist]'));
  const k = radios.indexOf(btn);
  let to = null;
  if (e.key === 'ArrowDown' || e.key === 'ArrowRight') to = radios[(k + 1) % radios.length];
  if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') to = radios[(k - 1 + radios.length) % radios.length];
  if (to) { e.preventDefault(); radios.forEach((r) => { r.tabIndex = r === to ? 0 : -1; }); to.focus(); }
  if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pickSort(btn); }
});

/* ---------- Help me choose tabs ---------- */
const selectTab = (tab, focus) => {
  const list = tab.closest('[role="tablist"]');
  const tabs = $$('[role="tab"]', list);
  tabs.forEach((t) => {
    const on = t === tab;
    t.setAttribute('aria-selected', on ? 'true' : 'false');
    t.tabIndex = on ? 0 : -1;
    const panel = document.getElementById(t.getAttribute('aria-controls'));
    if (panel) panel.hidden = !on;
  });
  if (focus) tab.focus();
};
document.addEventListener('click', (e) => {
  const tab = e.target.closest('[data-gvb-find] [role="tab"]');
  if (tab) selectTab(tab, false);
});
document.addEventListener('keydown', (e) => {
  const tab = e.target.closest && e.target.closest('[data-gvb-find] [role="tab"]');
  if (!tab) return;
  const tabs = $$('[role="tab"]', tab.closest('[role="tablist"]'));
  const k = tabs.indexOf(tab);
  let to = null;
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = tabs[(k + 1) % tabs.length];
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = tabs[(k - 1 + tabs.length) % tabs.length];
  if (e.key === 'Home') to = tabs[0];
  if (e.key === 'End') to = tabs[tabs.length - 1];
  if (to) { e.preventDefault(); selectTab(to, true); }
});

/* the head's "Help me choose": the panel when it exists, else the help card (#faq) */
const syncHelp = () => {
  const target = document.getElementById('find') ? '#find' : '#faq';
  $$('[data-gvb-help-entry]').forEach((a) => a.setAttribute('href', target));
};

/* ---------- sticky bar hairline ---------- */
let tick = false;
const stuck = () => {
  tick = false;
  $$('.gvb-coll [data-fbar]').forEach((bar) => {
    const top = parseFloat(getComputedStyle(bar).top) || 0;
    bar.classList.toggle('is-stuck', bar.getBoundingClientRect().top <= top + 0.5 && window.scrollY > 0);
  });
};
window.addEventListener('scroll', () => { if (!tick) { tick = true; requestAnimationFrame(stuck); } }, { passive: true });

/* ---------- studio ground for white-ground packshots (corner sampling, same-origin CDN) ---------- */
const canvas = document.createElement('canvas');
canvas.width = canvas.height = 16;
const ctx = canvas.getContext('2d', { willReadFrequently: true });
const classify = (img) => {
  const media = img.closest('.pcard-media');
  if (!media || media.dataset.studio) return;
  if (!img.complete || !img.naturalWidth) { img.addEventListener('load', () => classify(img), { once: true }); return; }
  media.dataset.studio = '0';
  try {
    ctx.clearRect(0, 0, 16, 16);
    ctx.drawImage(img, 0, 0, 16, 16);
    const d = ctx.getImageData(0, 0, 16, 16).data;
    let white = 0, n = 0;
    for (let y = 0; y < 16; y++) {
      for (let x = 0; x < 16; x++) {
        if (x > 0 && x < 15 && y > 0 && y < 15) continue;
        const i = (y * 16 + x) * 4, r = d[i], g = d[i + 1], b = d[i + 2];
        n++;
        if (r > 234 && g > 234 && b > 234 && Math.max(r, g, b) - Math.min(r, g, b) < 16) white++;
      }
    }
    if (white / n >= 0.9) { media.classList.add('is-studio'); media.dataset.studio = '1'; }
  } catch (err) { /* cross-origin image: keep the photo as it is */ }
};
const studio = (scope) => $$('.gvb-grid .pcard-media img.main', scope).forEach(classify);

/* ---------- after a facet update: focus, announcement, re-init ---------- */
const announce = (msg) => {
  const live = document.getElementById('status-announcement');
  if (live && msg) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); }
};
const afterUpdate = (sec) => {
  studio(sec);
  syncHelp();
  stuck();
  if (G && G.init) G.init(sec);
  if (!pending || pending.id !== sec.id) return;
  const key = pending.key;
  pending = null;
  const el = key && (sec.querySelector('[data-key="' + key + '"]') || (key === 'clear' && sec.querySelector('[data-key="sort"]')));
  if (el) el.focus({ preventScroll: true });
  const status = sec.querySelector('[data-gvb-status]');
  const count = sec.querySelector('.fbar-count');
  const empty = sec.querySelector('.grid-empty h2');
  announce(empty ? empty.textContent : status ? status.firstElementChild.textContent.replace(/\s+/g, ' ').trim() : count ? count.textContent.trim() : '');
};
const watch = (sec) => {
  if (!sec || sec._gvbWatch) return;
  sec._gvbWatch = new MutationObserver((list) => { if (list.some((m) => m.type === 'childList' && m.target === sec)) afterUpdate(sec); });
  sec._gvbWatch.observe(sec, { childList: true });
};

/* ---------- quick buy: close the sheet after an add ---------- */
window.addEventListener('variant:add', (e) => {
  const qb = e.target && e.target.closest ? e.target.closest('quick-buy-drawer[id^="quick-buy-gvb-"]') : null;
  if (!qb) return;
  qb.classList.add('gvb-qb-done');
  requestAnimationFrame(() => {
    const done = () => {
      qb.classList.remove('gvb-qb-done');
      if (G && G.store && G.store.after_add === 'drawer') {
        const drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.show === 'function') drawer.show();
      }
    };
    const p = typeof qb.hide === 'function' ? qb.hide() : null;
    if (p && p.then) p.then(done); else done();
  });
}, true);

/* ---------- init ---------- */
const init = (scope) => {
  $$('[data-gvb-coll]', scope).forEach((root) => watch(root.closest('.shopify-section')));
  studio(scope);
  syncHelp();
  stuck();
};
init(document);
document.addEventListener('shopify:section:load', (e) => init(e.target));
